import React, { useRef, useEffect, useState } from "react";
import { Marker } from "react-map-gl"; // Replace with your Marker component
import VideocamIcon from "@mui/icons-material/Videocam";
import PlaceIcon from "@mui/icons-material/Place";
import CancelIcon from "@mui/icons-material/Cancel";

const MarkerComponent = ({
  marker,
  deleteMarker,
  setInteractive,
  updateDirection,
  isMobile,
  isPhoto,
}) => {
  const markerId = marker.properties.id;
  const containerRef = useRef(null);
  const positions = marker.properties.arrowProperties || {
    x: 0,
    y: -30,
    angle: 270,
  };
  const arrowPosRef = useRef(positions);
  const draggingRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  const didInit = useRef(false);

  const computeAngle = (x, y) => Math.atan2(y, x) * (180 / Math.PI);

  // Updated drag function to enforce a minimum distance from the center.
  const handleDragMove = (clientX, clientY) => {
    if (!draggingRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;

    // Define your min and max radius for dragging
    const maxRadius = 40;
    const minRadius = 30; // Prevent camera icon from getting too close to the marker center
    const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);
    let clampedX, clampedY;

    if (distance === 0) {
      // Default to minRadius horizontally if there's no movement yet.
      clampedX = minRadius;
      clampedY = 0;
    } else if (distance < minRadius) {
      // Clamp to the minimum radius
      const ratio = minRadius / distance;
      clampedX = deltaX * ratio;
      clampedY = deltaY * ratio;
    } else if (distance > maxRadius) {
      // Clamp to the maximum radius
      const ratio = maxRadius / distance;
      clampedX = deltaX * ratio;
      clampedY = deltaY * ratio;
    } else {
      // Allow the actual drag distance if within allowed range
      clampedX = deltaX;
      clampedY = deltaY;
    }

    // Update the arrow's position and compute the new angle
    arrowPosRef.current = {
      x: clampedX,
      y: clampedY,
      angle: computeAngle(clampedX, clampedY),
    };

    // Update CSS custom properties for smooth re-rendering
    containerRef.current.style.setProperty(
      "--arrow-x",
      `calc(50% + ${arrowPosRef.current.x}px)`
    );
    containerRef.current.style.setProperty(
      "--arrow-y",
      `calc(50% + ${arrowPosRef.current.y}px)`
    );
    containerRef.current.style.setProperty(
      "--arrow-angle",
      `${arrowPosRef.current.angle}deg`
    );
  };

  // Mouse event handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    setInteractive(false);

    // Attach global listeners so dragging works even if the pointer leaves the marker
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingRef.current) {
      draggingRef.current = false;
      setInteractive(true);
      updateDirection(markerId, { ...arrowPosRef.current });
    }
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  // Touch event handlers
  const handleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    setInteractive(false);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingRef.current) {
      draggingRef.current = false;
      setInteractive(true);
      updateDirection(markerId, { ...arrowPosRef.current });
    }
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  // Run updateDirection only once on mount
  useEffect(() => {
    if (!didInit.current) {
      updateDirection(markerId, { ...arrowPosRef.current });
      didInit.current = true;
    }
  }, [markerId, updateDirection]);

  return (
    <Marker
      key={markerId}
      longitude={marker.geometry.coordinates[0]}
      latitude={marker.geometry.coordinates[1]}
      anchor="bottom"
    >
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "50px",
          height: "50px",
          textAlign: "center",
          "--arrow-x": `calc(50% + ${arrowPosRef.current.x}px)`,
          "--arrow-y": `calc(50% + ${arrowPosRef.current.y}px)`,
          "--arrow-angle": `${arrowPosRef.current.angle || 270}deg`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Draggable Camera Icon */}
        {isPhoto && (
          <div
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{
              position: "absolute",
              left: "var(--arrow-x)",
              top: "var(--arrow-y)",
              transform: "translate(-50%, -50%) rotate(var(--arrow-angle))",
              width: "25px",
              height: "25px",
              cursor: draggingRef.current ? "grabbing" : "grab",
              color: "#009eff",
              zIndex: 100,
            }}
          >
            <VideocamIcon />
          </div>
        )}

        {/* Marker Icon (centered) */}
        <PlaceIcon
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 30,
            height: 30,
            color: "rgb(255, 134, 52)",
          }}
        />

        {/* Delete Button */}
        {(isHovered || isMobile) && (
          <CancelIcon
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteMarker(marker);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteMarker(marker);
            }}
            style={{
              position: "absolute",
              top: "-6px",
              right: "0",
              width: "15px",
              height: "20px",
              color: "red",
              cursor: "pointer",
            }}
          />
        )}
      </div>
    </Marker>
  );
};

export default MarkerComponent;
